import React, { ElementType } from 'react'
import { useTranslation } from 'react-i18next'
import { useEnvironment } from '@hooks/useEnvironment'
import { SubmitError, SubmitErrorType } from '@typesApp/SubmitError'
import { FooterAction } from '@components/ui/Modal/FooterAction'
import { BasicErrorModal, DualActionErrorModal } from './BasicErrorModal'
import { injectDataAttributes } from '@utils/dataAttributes'

interface QuoteErrorModalProps {
  onBackToForm: () => void
  onProceedToCheckout: () => void
  error: SubmitError
}

export const QuoteErrorModal: React.FC<QuoteErrorModalProps> = (props) => {
  const { error } = props

  const modals = {
    REQUEST_QUOTE_ERROR: RequestQuoteErrorModal,
    REQUEST_QUOTE_RESTRICTED: QuoteRestrictedModal,
    SUBMIT_QUOTE_ERROR: SubmitQuoteErrorModal,
    SUBMIT_QUOTE_NO_RETRY_ERROR: SubmitQuoteNoRetryErrorModal,
    INSUFFICIENT_COVERAGE: InsufficientCoverageErrorModal,
  } as Record<SubmitErrorType, ElementType>

  const ErrorModal = modals[error.type]
  return ErrorModal ? <ErrorModal {...props} /> : null
}

const RequestQuoteErrorModal: React.FC<QuoteErrorModalProps> = (props) => {
  const { onBackToForm, onProceedToCheckout, error } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError' })

  return (
    <DualActionErrorModal
      open={error.type === SubmitErrorType.REQUEST_QUOTE_ERROR}
      onPrimaryAction={onBackToForm}
      onSecondaryAction={onProceedToCheckout}
      options={{
        title: t('requestQuote.title'),
        message: t('requestQuote.message'),
        details: error.details,
        labels: {
          primary: t('requestQuote.back-cta'),
          secondary: t('requestQuote.proceed-cta')
        },
        dataAttributes: {
          primary: injectDataAttributes('modal.backToForm'),
          secondary: injectDataAttributes('modal.proceedToCheckout')
        }
      }}
    />
  )
}

const SubmitQuoteNoRetryErrorModal: React.FC<QuoteErrorModalProps> = (props) => {
  const { onProceedToCheckout, error } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError' })

  return (
    <BasicErrorModal
      open={error.type === SubmitErrorType.SUBMIT_QUOTE_NO_RETRY_ERROR}
      onClose={onProceedToCheckout}
      title={t('submitQuote.title')}
      message={t('submitQuote.message')}
      footer={
        <FooterAction
          onClick={onProceedToCheckout}
          renderAs='button'
          label={t('submitQuote.proceed-cta')}
          dataAttributes={injectDataAttributes('modal.proceedToCheckout')}
        />
      }
    />
  )
}

const SubmitQuoteErrorModal: React.FC<QuoteErrorModalProps> = (props) => {
  const { onBackToForm, onProceedToCheckout, error } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError' })

  return (
    <DualActionErrorModal
      open={error.type === SubmitErrorType.SUBMIT_QUOTE_ERROR}
      onPrimaryAction={onBackToForm}
      onSecondaryAction={onProceedToCheckout}
      options={{
        title: t('submitQuote.title'),
        message: t('submitQuote.message'),
        details: error.details,
        labels: {
          primary: t('submitQuote.back-cta'),
          secondary: t('submitQuote.proceed-cta')
        },
        dataAttributes: {
          primary: injectDataAttributes('modal.backToForm'),
          secondary: injectDataAttributes('modal.proceedToCheckout')
        }
      }}
    />
  )
}

const InsufficientCoverageErrorModal: React.FC<QuoteErrorModalProps> = (props) => {
  const { onProceedToCheckout, error } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError' })

  return (
    <DualActionErrorModal
      open={error.type === SubmitErrorType.INSUFFICIENT_COVERAGE}
      onPrimaryAction={onProceedToCheckout}
      options={{
        title: t('insufficientCoverage.title'),
        message: t('insufficientCoverage.message'),
        details: error.details,
        labels: {
          primary: t('insufficientCoverage.proceed-cta'),
        },
        dataAttributes: {
          primary: injectDataAttributes('modal.proceedToCheckout'),
        }
      }}
    />
  )
}

const QuoteRestrictedModal: React.FC<QuoteErrorModalProps> = (props) => {
  const { onProceedToCheckout } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace, { keyPrefix: 'SubmitError.quoteRestricted' })

  return (
    <BasicErrorModal
      open={true}
      onClose={onProceedToCheckout}
      title={t('title')}
      message={t('message')}
      footer={
        <FooterAction
          onClick={onProceedToCheckout}
          renderAs='button'
          label={t('ok-cta')}
          dataAttributes={injectDataAttributes('modal.ok')}
        />
      }
    />
  )
}
